import JSZip from 'jszip';

// ═══════════════════════════════════════════════════════════════
// Čtení metadat obrázků přímo z OOXML (Blok 5+, doplnění importu).
//
// Mammoth ve svém convertImage callbacku NEVYSTAVUJE rozměry (w:ext) ani
// režim obtékání (wp:anchor / wrap*). Abychom je při importu zachovali,
// otevřeme si `word/document.xml` sami (JSZip, který už je v závislostech
// docx.js) a posbíráme metadata obrázků v POŘADÍ výskytu. Import je pak
// napáruje na <img> uzly (mammoth generuje <img> ve stejném pořadí).
//
// Mapování wrap ← OOXML:
//   <wp:inline>                          → 'inline'
//   <wp:anchor> + wrapSquare wrapText=right  → 'left'  (obrázek vlevo, text vpravo)
//   <wp:anchor> + wrapSquare wrapText=left   → 'right'
//   <wp:anchor> + wrapTopAndBottom           → 'block' (+ align z positionH)
//   <wp:anchor> + jiné/žádné wrap            → 'block' (bezpečný default)
//
// Rozměry: <wp:extent cx cy> v EMU → px (1 px = 9525 EMU při 96 dpi).
// ═══════════════════════════════════════════════════════════════

const EMU_PER_PX = 9525;

export type ImgWrap = 'inline' | 'block' | 'left' | 'right';
export type ImgAlign = 'left' | 'center' | 'right';

export interface ImageMeta {
  width: number | null;
  height: number | null;
  wrap: ImgWrap;
  align: ImgAlign;
}

/**
 * Vytáhne metadata všech obrázků z DOCX v pořadí výskytu v těle dokumentu.
 * Vrací prázdné pole, pokud se dokument nepodaří přečíst (import pak jede
 * dál bez obohacení — obrázky zůstanou inline v přirozené velikosti).
 */
export async function extractImageMeta(buffer: ArrayBuffer): Promise<ImageMeta[]> {
  let xml: string;
  try {
    const zip = await JSZip.loadAsync(buffer);
    const doc = zip.file('word/document.xml');
    if (!doc) return [];
    xml = await doc.async('string');
  } catch {
    return [];
  }

  const metas: ImageMeta[] = [];
  // Každý obrázek je zabalený v <w:drawing>…</w:drawing> (inline i anchor).
  const drawingRe = /<w:drawing\b[\s\S]*?<\/w:drawing>/g;
  let m: RegExpExecArray | null;
  while ((m = drawingRe.exec(xml)) !== null) {
    metas.push(parseDrawing(m[0]));
  }
  return metas;
}

function parseDrawing(d: string): ImageMeta {
  // Rozměry z <wp:extent cx="" cy="">
  const ext = d.match(/<wp:extent\b[^>]*\bcx="(\d+)"[^>]*\bcy="(\d+)"/);
  const width = ext ? emuToPx(ext[1]) : null;
  const height = ext ? emuToPx(ext[2]) : null;

  const isAnchor = /<wp:anchor\b/.test(d);
  if (!isAnchor) {
    return { width, height, wrap: 'inline', align: 'left' };
  }

  // Anchor: urči typ obtékání.
  let wrap: ImgWrap;
  let align: ImgAlign = 'left';

  if (/<wp:wrapSquare\b/.test(d) || /<wp:wrapTight\b/.test(d) || /<wp:wrapThrough\b/.test(d)) {
    // wrapText určuje, na které straně obrázku text obtéká.
    const side = d.match(/wrapText="(\w+)"/);
    const st = side ? side[1] : 'bothSides';
    // wrapText="right" → text je vpravo od obrázku → obrázek je vlevo → 'left'
    wrap = st === 'left' ? 'right' : 'left';
  } else {
    // wrapTopAndBottom, wrapNone i cokoli jiného → chováme se jako block.
    wrap = 'block';
    align = readHAlign(d);
  }

  return { width, height, wrap, align };
}

/** Přečte vodorovné zarovnání z <wp:positionH><wp:align>…</wp:align>. */
function readHAlign(d: string): ImgAlign {
  const m = d.match(/<wp:positionH\b[\s\S]*?<wp:align>(\w+)<\/wp:align>/);
  const a = m ? m[1] : 'left';
  if (a === 'center') return 'center';
  if (a === 'right') return 'right';
  return 'left';
}

function emuToPx(emu: string): number {
  return Math.round(parseInt(emu, 10) / EMU_PER_PX);
}
